import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  AlertCircle, BookMarked, Crown, Feather, FileText, Lightbulb,
  MapPin, Plus, RefreshCw, RotateCw, Save, Sparkles, Target,
  Trash2, Upload, Users, Wand2, X, Zap
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Card, CardContent } from "../ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import SpeechToTextButton from "../SpeechToTextButton";

export const StoryBibleModal = ({
  open,
  onOpenChange,
  storyBible,
  setStoryBible,
  project,
  setProject,
  projectId,
  API,
  loadStoryBible,
  tensionLevelColors,
  tensionLevelNames,
  tensionTemplates,
  onShowTensionModal,
  onShowForeshadowingModal,
  loadTensionCurve,
}) => {
  const [loadingBible, setLoadingBible] = useState(false);
  const [scanProgress, setScanProgress] = useState("");
  const [refreshingOutline, setRefreshingOutline] = useState(false);
  const [refreshProgress, setRefreshProgress] = useState("");
  const [chapterSummaries, setChapterSummaries] = useState([]);
  const [loadingSummaries, setLoadingSummaries] = useState(false);

  useEffect(() => {
    if (open) {
      loadChapterSummaries();
    }
  }, [open]);

  const loadChapterSummaries = async () => {
    setLoadingSummaries(true);
    try {
      const res = await axios.get(`${API}/chapter-summaries/${projectId}`);
      setChapterSummaries(res.data.summaries || []);
    } catch (error) {
      console.error("Failed to load chapter summaries:", error);
    } finally {
      setLoadingSummaries(false);
    }
  };

  const regenerateSummary = async (chapterNumber) => {
    try {
      const res = await axios.post(`${API}/chapter-summaries/${projectId}/generate/${chapterNumber}`);
      if (res.data.success) {
        setChapterSummaries(prev => {
          const filtered = prev.filter(s => s.chapter_number !== chapterNumber);
          return [...filtered, res.data.summary].sort((a, b) => a.chapter_number - b.chapter_number);
        });
        toast.success(`Summary for Chapter ${chapterNumber} regenerated!`);
      }
    } catch (error) {
      toast.error("Failed to regenerate summary");
    }
  };

  const addExclusion = async (exclusion) => {
    try {
      await axios.post(`${API}/story-bible/add-exclusion?project_id=${projectId}&exclusion=${encodeURIComponent(exclusion)}`);
      setStoryBible(prev => ({
        ...prev,
        exclusions: [...(prev?.exclusions || []), exclusion]
      }));
      toast.success(`Added exclusion: "${exclusion}"`);
    } catch (error) {
      toast.error("Failed to add exclusion");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-5xl flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-literary flex items-center gap-2">
              <BookMarked className="w-5 h-5 text-purple-600" />
              Story Bible
            </DialogTitle>
            <DialogDescription className="text-left">
              Your story's memory bank. Everything here is passed to Shweebum when writing each chapter.
            </DialogDescription>
          </DialogHeader>
          
          {/* Explanation Banner */}
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-3 text-sm">
            <p className="text-purple-800 font-medium mb-1">📖 How Story Bible Works:</p>
            <ul className="text-purple-700 text-xs space-y-1">
              <li>• <strong>Characters & plot threads</strong> you add here appear in every chapter plan</li>
              <li>• <strong>"Never Include"</strong> items are blocked from all future chapters</li>
              <li>• <strong>Chapter summaries</strong> are auto-generated after each chapter to maintain continuity</li>
              <li>• <strong>Tension curve</strong> guides the emotional pacing of your entire story</li>
            </ul>
            <p className="text-purple-600 text-xs mt-2 italic">Tip: Add important characters here so Shweebum remembers them throughout your book!</p>
          </div>
          
          <div className="flex-1 overflow-y-auto space-y-4 py-4">
            {loadingBible ? (
              <div className="text-center py-8">
                <RefreshCw className="w-8 h-8 animate-spin text-purple-500 mb-2" />
                <p className="text-muted-foreground">Loading Story Bible...</p>
              </div>
            ) : storyBible ? (
              <>
                {/* Auto-Scan Story Button */}
                <div className="flex gap-2 mb-4">
                  <Button
                    variant="outline"
                    className="flex-1 bg-gradient-to-r from-purple-100 to-indigo-100 border-purple-300 hover:from-purple-200 hover:to-indigo-200"
                    onClick={async () => {
                      if (!project?.content || project.content.length < 200) {
                        toast.error("Write more content first so Shweebum can analyze it");
                        return;
                      }
                      setLoadingBible(true);
                      setScanProgress('Starting scan...');
                      try {
                        const response = await fetch(`${API}/story-bible/${projectId}/scan`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ content: project.content })
                        });
                        if (!response.ok) throw new Error('Scan failed');
                        
                        const reader = response.body.getReader();
                        const decoder = new TextDecoder();
                        
                        while (true) {
                          const { done, value } = await reader.read();
                          if (done) break;
                          
                          const chunk = decoder.decode(value);
                          const lines = chunk.split('\n');
                          
                          for (const line of lines) {
                            if (line.startsWith('data: ')) {
                              try {
                                const data = JSON.parse(line.slice(6));
                                if (data.type === 'progress') {
                                  setScanProgress(data.message);
                                } else if (data.type === 'done') {
                                  setStoryBible(data.story_bible);
                                  toast.success("Story Bible updated from your story!");
                                  setScanProgress('');
                                } else if (data.type === 'error') {
                                  toast.error(data.message || "Scan failed");
                                  setScanProgress('');
                                }
                              } catch (e) {}
                            }
                          }
                        }
                      } catch (e) {
                        toast.error("Failed to scan story");
                        setScanProgress('');
                      } finally {
                        setLoadingBible(false);
                      }
                    }}
                    disabled={loadingBible}
                  >
                    {loadingBible ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                    Auto-Populate from Story
                  </Button>
                  {scanProgress && (
                    <p className="text-xs text-purple-600 mt-1 animate-pulse">{scanProgress}</p>
                  )}
                </div>
                
                {/* Refresh Chapter List Button */}
                <div className="flex flex-col gap-2">
                  <Button
                    variant="outline"
                    className="w-full bg-gradient-to-r from-cyan-900/20 to-blue-900/20 border-cyan-500/30 hover:from-cyan-900/30 hover:to-blue-900/30"
                    onClick={async () => {
                      if (!project?.chapter_outline?.length) {
                        toast.error("No chapter outline to refresh. Create a book with 'I Have an Idea' first.");
                        return;
                      }
                      setRefreshingOutline(true);
                      setRefreshProgress('Starting refresh...');
                      playTick();
                      try {
                        const response = await fetch(`${API}/story-bible/${projectId}/refresh-outline-stream`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' }
                        });
                        if (!response.ok) throw new Error('Refresh failed');
                        
                        const reader = response.body.getReader();
                        const decoder = new TextDecoder();
                        
                        while (true) {
                          const { done, value } = await reader.read();
                          if (done) break;
                          
                          const chunk = decoder.decode(value);
                          const lines = chunk.split('\n');
                          
                          for (const line of lines) {
                            if (line.startsWith('data: ')) {
                              try {
                                const data = JSON.parse(line.slice(6));
                                if (data.type === 'progress') {
                                  setRefreshProgress(data.message);
                                  playTick();
                                } else if (data.type === 'partial') {
                                  // Partial chapters received
                                  playTick();
                                } else if (data.type === 'done') {
                                  setProject(prev => ({ ...prev, chapter_outline: data.chapters }));
                                  playComplete();
                                  toast.success("Chapter outline refreshed based on your Story Bible!");
                                  setRefreshProgress('');
                                } else if (data.type === 'error') {
                                  toast.error(data.message || "Refresh failed");
                                  setRefreshProgress('');
                                }
                              } catch (e) {}
                            }
                          }
                        }
                      } catch (e) {
                        toast.error("Failed to refresh outline");
                        setRefreshProgress('');
                      } finally {
                        setRefreshingOutline(false);
                      }
                    }}
                    disabled={refreshingOutline || !project?.chapter_outline?.length}
                    data-testid="refresh-outline-btn"
                  >
                    {refreshingOutline ? <RotateCw className="w-4 h-4 mr-2 animate-spin" /> : <RotateCw className="w-4 h-4 mr-2" />}
                    Refresh Chapter List from Bible
                  </Button>
                  {refreshProgress && (
                    <p className="text-xs text-cyan-400 mt-1 animate-pulse">{refreshProgress}</p>
                  )}
                  <p className="text-xs text-muted-foreground italic">
                    Regenerates your chapter outline using characters, plot threads, and settings from your Story Bible.
                  </p>

                  {/* Apply Bible Changes to Story Button */}
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full mt-2 border-green-300 text-green-700 hover:bg-green-50"
                    onClick={async () => {
                      try {
                        setRefreshProgress('Scanning story for inconsistencies...');
                        const res = await axios.post(`${API}/story-bible/${projectId}/apply-to-story`);
                        if (res.data.changes_made > 0) {
                          // Refresh project to get updated content
                          const projectRes = await axios.get(`${API}/project/${projectId}`);
                          setProject(projectRes.data);
                          toast.success(`Applied ${res.data.changes_made} consistency fixes to your story!`);
                        } else {
                          toast.info("Your story already matches the Story Bible - no changes needed!");
                        }
                      } catch (e) {
                        toast.error("Failed to apply changes");
                      } finally {
                        setRefreshProgress('');
                      }
                    }}
                    disabled={refreshingOutline || !project?.content}
                    data-testid="apply-bible-to-story-btn"
                  >
                    <Wand2 className="w-4 h-4 mr-2" />
                    Apply Bible to Story
                  </Button>
                  <p className="text-xs text-muted-foreground italic">
                    Scans your story and fixes any inconsistencies with your Story Bible (names, traits, excluded content).
                  </p>
                  
                  {/* Re-Sync Chapter Outline from File Upload */}
                  <div className="mt-4 pt-4 border-t border-purple-200">
                    <h5 className="text-sm font-medium text-purple-800 mb-2 flex items-center gap-2">
                      <Upload className="w-4 h-4" /> Re-Sync Chapter Outline
                    </h5>
                    <p className="text-xs text-muted-foreground mb-2">
                      If the AI isn't following your outline, re-upload your chapter outline file to force exact verbatim text.
                    </p>
                    <input
                      type="file"
                      accept=".pdf,.docx,.txt"
                      className="hidden"
                      id="resync-chapters-upload"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        
                        setRefreshProgress('Re-syncing chapter outline...');
                        setRefreshingOutline(true);
                        
                        try {
                          const formData = new FormData();
                          formData.append('files', file);
                          
                          const res = await axios.post(`${API}/resync-chapters/${projectId}`, formData, {
                            headers: { 'Content-Type': 'multipart/form-data' },
                            timeout: 120000
                          });
                          
                          if (res.data.success) {
                            // Refresh project to get updated chapter_outline
                            const projectRes = await axios.get(`${API}/project/${projectId}`);
                            setProject(projectRes.data);
                            
                            // Update story bible if we have one
                            if (storyBible) {
                              setStoryBible(prev => ({
                                ...prev,
                                raw_chapter_texts: res.data.raw_chapter_texts || {},
                                chapter_outlines: res.data.chapter_outline || []
                              }));
                            }
                            
                            toast.success(`Re-synced ${res.data.chapters_found} chapters (${res.data.total_outline_words?.toLocaleString()} words). AI will now follow your exact outline!`);
                          } else {
                            toast.error(res.data.error || 'Re-sync failed');
                          }
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Failed to re-sync chapters');
                        } finally {
                          setRefreshingOutline(false);
                          setRefreshProgress('');
                          e.target.value = ''; // Reset file input
                        }
                      }}
                      data-testid="resync-chapters-file-input"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full border-blue-300 text-blue-700 hover:bg-blue-50"
                      onClick={() => document.getElementById('resync-chapters-upload')?.click()}
                      disabled={refreshingOutline}
                      data-testid="resync-chapters-btn"
                    >
                      {refreshingOutline ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          {refreshProgress || 'Processing...'}
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          Upload Outline File (.pdf, .docx, .txt)
                        </>
                      )}
                    </Button>
                    <p className="text-[10px] text-blue-600 mt-1">
                      ⚡ This stores your EXACT outline text. The AI will read your words verbatim, not a summary.
                    </p>
                  </div>
                </div>

                {/* Protagonist Section - Enhanced */}
                <Card className="border-purple-200 bg-purple-50">
                  <CardContent className="p-4">
                    <h4 className="font-semibold text-purple-800 mb-3 flex items-center gap-2">
                      <Crown className="w-4 h-4" /> Protagonist (POV Character)
                    </h4>
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-2">
                        <Input
                          placeholder="Name *"
                          value={storyBible.protagonist?.name || ''}
                          onChange={(e) => setStoryBible({
                            ...storyBible,
                            protagonist: { ...storyBible.protagonist, name: e.target.value }
                          })}
                          className="bg-card"
                        />
                        <Input
                          placeholder="Age"
                          value={storyBible.protagonist?.age || ''}
                          onChange={(e) => setStoryBible({
                            ...storyBible,
                            protagonist: { ...storyBible.protagonist, age: e.target.value }
                          })}
                          className="bg-card"
                        />
                      </div>
                      <div className="relative">
                        <Textarea
                          placeholder="Physical appearance and personality"
                          value={storyBible.protagonist?.description || ''}
                          onChange={(e) => setStoryBible({
                            ...storyBible,
                            protagonist: { ...storyBible.protagonist, description: e.target.value }
                          })}
                          className="bg-card min-h-[50px] pr-10"
                        />
                        <SpeechToTextButton
                          onTranscript={(text, append) => {
                            const current = storyBible.protagonist?.description || '';
                            setStoryBible({
                              ...storyBible,
                              protagonist: { 
                                ...storyBible.protagonist, 
                                description: append && current ? current + ' ' + text : text 
                              }
                            });
                          }}
                          className="absolute right-2 top-2 h-6 w-6"
                          size="icon"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Input
                          placeholder="Motivation (What do they want?)"
                          value={storyBible.protagonist?.motivation || ''}
                          onChange={(e) => setStoryBible({
                            ...storyBible,
                            protagonist: { ...storyBible.protagonist, motivation: e.target.value }
                          })}
                          className="bg-card text-sm"
                        />
                        <Input
                          placeholder="Goal (What are they trying to achieve?)"
                          value={storyBible.protagonist?.goal || ''}
                          onChange={(e) => setStoryBible({
                            ...storyBible,
                            protagonist: { ...storyBible.protagonist, goal: e.target.value }
                          })}
                          className="bg-card text-sm"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Input
                          placeholder="Fear/Weakness"
                          value={storyBible.protagonist?.fear || ''}
                          onChange={(e) => setStoryBible({
                            ...storyBible,
                            protagonist: { ...storyBible.protagonist, fear: e.target.value }
                          })}
                          className="bg-card text-sm"
                        />
                        <Input
                          placeholder="Character Arc (How they change)"
                          value={storyBible.protagonist?.arc || ''}
                          onChange={(e) => setStoryBible({
                            ...storyBible,
                            protagonist: { ...storyBible.protagonist, arc: e.target.value }
                          })}
                          className="bg-card text-sm"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Active Characters - Enhanced with details */}
                <Card>
                  <CardContent className="p-4">
                    <h4 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                      <Users className="w-4 h-4" /> Characters ({storyBible.active_characters?.length || 0})
                    </h4>
                    {storyBible.active_characters?.length > 0 && (
                      <div className="space-y-2 mb-3 max-h-64 overflow-y-auto">
                        {storyBible.active_characters.map((char, i) => (
                          <div key={i} className="bg-background p-3 rounded border border-border">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <strong className="text-foreground">{char.name}</strong>
                                  <Badge variant="outline" className="text-xs">{char.role || 'character'}</Badge>
                                  {char.age && <span className="text-xs text-muted-foreground">Age: {char.age}</span>}
                                </div>
                                {char.description && <p className="text-xs text-muted-foreground mt-1">{char.description}</p>}
                                {(char.motivation || char.goal || char.fear) && (
                                  <div className="mt-2 text-xs space-y-0.5">
                                    {char.motivation && <p className="text-blue-700"><strong>Wants:</strong> {char.motivation}</p>}
                                    {char.goal && <p className="text-green-700"><strong>Goal:</strong> {char.goal}</p>}
                                    {char.fear && <p className="text-red-700"><strong>Fear:</strong> {char.fear}</p>}
                                    {char.arc && <p className="text-purple-700"><strong>Arc:</strong> {char.arc}</p>}
                                  </div>
                                )}
                              </div>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 w-6 p-0 text-red-500 hover:text-red-700 flex-shrink-0"
                                onClick={() => setStoryBible({
                                  ...storyBible,
                                  active_characters: storyBible.active_characters.filter((_, idx) => idx !== i)
                                })}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Add Character Form - Collapsible */}
                    <details className="mt-2">
                      <summary className="text-sm text-muted-foreground cursor-pointer hover:text-foreground flex items-center gap-1">
                        <Plus className="w-3 h-3" /> Add New Character
                      </summary>
                      <div className="mt-2 space-y-2 p-2 bg-background rounded">
                        <div className="grid grid-cols-2 gap-2">
                          <Input placeholder="Name *" id="new-char-name" className="text-sm" />
                          <Input placeholder="Role (villain, mentor, ally)" id="new-char-role" className="text-sm" />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <Input placeholder="Age" id="new-char-age" className="text-sm" />
                          <Input placeholder="Motivation (What do they want?)" id="new-char-motivation" className="text-sm" />
                        </div>
                        <Input placeholder="Goal (What are they trying to achieve?)" id="new-char-goal" className="text-sm" />
                        <Input placeholder="Fear/Weakness" id="new-char-fear" className="text-sm" />
                        <Textarea placeholder="Brief description" id="new-char-desc" className="text-sm min-h-[50px]" />
                        <Input placeholder="Character Arc (How do they change?)" id="new-char-arc" className="text-sm" />
                        <Button
                          size="sm"
                          className="w-full"
                          onClick={() => {
                            const name = document.getElementById('new-char-name').value.trim();
                            const role = document.getElementById('new-char-role').value.trim();
                            if (name) {
                              setStoryBible({
                                ...storyBible,
                                active_characters: [...(storyBible.active_characters || []), { 
                                  name, 
                                  role: role || 'character',
                                  age: document.getElementById('new-char-age').value.trim(),
                                  motivation: document.getElementById('new-char-motivation').value.trim(),
                                  goal: document.getElementById('new-char-goal').value.trim(),
                                  fear: document.getElementById('new-char-fear').value.trim(),
                                  description: document.getElementById('new-char-desc').value.trim(),
                                  arc: document.getElementById('new-char-arc').value.trim(),
                                  status: 'alive' 
                                }]
                              });
                              // Clear all fields
                              ['name', 'role', 'age', 'motivation', 'goal', 'fear', 'desc', 'arc'].forEach(f => {
                                const el = document.getElementById(`new-char-${f}`);
                                if (el) el.value = '';
                              });
                            }
                          }}
                        >
                          <Plus className="w-4 h-4 mr-1" /> Add Character
                        </Button>
                      </div>
                    </details>
                  </CardContent>
                </Card>

                {/* Open Plot Threads - Editable */}
                <Card>
                  <CardContent className="p-4">
                    <h4 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                      <Target className="w-4 h-4" /> Open Plot Threads ({storyBible.open_plot_threads?.length || 0})
                    </h4>
                    {storyBible.open_plot_threads?.length > 0 && (
                      <div className="space-y-2 mb-3">
                        {storyBible.open_plot_threads.map((thread, i) => (
                          <div key={i} className="flex items-center justify-between text-sm bg-background p-2 rounded">
                            <span>{thread.description || thread}</span>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                              onClick={() => setStoryBible({
                                ...storyBible,
                                open_plot_threads: storyBible.open_plot_threads.filter((_, idx) => idx !== i)
                              })}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="flex gap-2">
                      <div className="flex-1 relative">
                        <Input
                          placeholder="Add a plot thread (e.g., Who killed the mayor?)"
                          id="new-plot-thread"
                          className="pr-9"
                        />
                        <SpeechToTextButton
                          onTranscript={(text) => {
                            const input = document.getElementById('new-plot-thread');
                            if (input) {
                              input.value = text;
                              input.dispatchEvent(new Event('input', { bubbles: true }));
                            }
                          }}
                          appendMode={false}
                          className="absolute right-1.5 top-1/2 -translate-y-1/2 h-6 w-6"
                          size="icon"
                        />
                      </div>
                      <Button
                        size="sm"
                        onClick={() => {
                          const desc = document.getElementById('new-plot-thread').value;
                          if (desc) {
                            setStoryBible({
                              ...storyBible,
                              open_plot_threads: [...(storyBible.open_plot_threads || []), { description: desc, importance: 'major' }]
                            });
                            document.getElementById('new-plot-thread').value = '';
                          }
                        }}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Exclusions - Editable */}
                <Card className="border-red-200 bg-red-50">
                  <CardContent className="p-4">
                    <h4 className="font-semibold text-red-800 mb-2 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" /> Never Include (Exclusions)
                    </h4>
                    {storyBible.exclusions?.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {storyBible.exclusions.map((excl, i) => (
                          <Badge key={i} variant="outline" className="border-red-300 text-red-700">{excl}</Badge>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-red-700 italic">No exclusions set</p>
                    )}
                    <div className="mt-3 flex gap-2">
                      <input 
                        type="text" 
                        placeholder="Add exclusion (e.g., 'no zombies')" 
                        className="flex-1 text-sm border rounded px-2 py-1"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' && e.target.value.trim()) {
                            addExclusion(e.target.value.trim());
                            e.target.value = '';
                          }
                        }}
                      />
                      <Button size="sm" variant="outline" className="text-red-600 border-red-300" onClick={(e) => {
                        const input = e.target.parentElement.querySelector('input');
                        if (input.value.trim()) {
                          addExclusion(input.value.trim());
                          input.value = '';
                        }
                      }}>Add</Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Genre/Tone Lock */}
                <Card>
                  <CardContent className="p-4">
                    <h4 className="font-semibold text-foreground mb-2">Genre & Tone</h4>
                    <p className="text-sm text-muted-foreground">{storyBible.genre_tone_lock || `${project?.genre} - ${project?.tone || 'balanced'}`}</p>
                  </CardContent>
                </Card>

                {/* Writing Style & Narration - NEW */}
                <Card className="border-cyan-500/25 bg-slate-800/50">
                  <CardContent className="p-4">
                    <h4 className="font-semibold text-cyan-200 mb-3 flex items-center gap-2">
                      <Feather className="w-4 h-4" /> Writing Style & Narration
                    </h4>
                    
                    {/* POV Selection */}
                    <div className="mb-4">
                      <label className="text-sm font-medium text-cyan-100 block mb-2">Point of View (POV)</label>
                      <Select 
                        value={storyBible.narration_style?.pov || 'third_person_limited'} 
                        onValueChange={(v) => setStoryBible({
                          ...storyBible,
                          narration_style: { ...storyBible.narration_style, pov: v }
                        })}
                      >
                        <SelectTrigger className="bg-card" data-testid="pov-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="first_person">First Person ("I walked into the room...")</SelectItem>
                          <SelectItem value="third_person_limited">Third Person Limited (follows one character's perspective)</SelectItem>
                          <SelectItem value="third_person_omniscient">Third Person Omniscient (all-knowing narrator)</SelectItem>
                          <SelectItem value="third_person_objective">Third Person Objective (observer, no inner thoughts)</SelectItem>
                          <SelectItem value="second_person">Second Person ("You walk into the room...")</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-cyan-300 mt-1">
                        {storyBible.narration_style?.pov === 'first_person' && "The protagonist tells their own story directly."}
                        {storyBible.narration_style?.pov === 'third_person_limited' && "Reader sees through one character's eyes, but in third person."}
                        {storyBible.narration_style?.pov === 'third_person_omniscient' && "Narrator knows all characters' thoughts and feelings."}
                        {storyBible.narration_style?.pov === 'third_person_objective' && "Like a camera - only actions and dialogue, no inner thoughts."}
                        {storyBible.narration_style?.pov === 'second_person' && "Rare style that addresses the reader as the protagonist."}
                        {!storyBible.narration_style?.pov && "Reader sees through one character's eyes, but in third person."}
                      </p>
                    </div>

                    {/* Writing Style Preferences */}
                    <div className="mb-4">
                      <label className="text-sm font-medium text-cyan-100 block mb-2">Writing Style Rules</label>
                      <div className="space-y-2">
                        {(storyBible.narration_style?.style_rules || []).map((rule, i) => (
                          <div key={i} className="flex items-center justify-between text-sm bg-card p-2 rounded border border-cyan-500/25">
                            <span className="text-cyan-100">{rule}</span>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                              onClick={() => setStoryBible({
                                ...storyBible,
                                narration_style: {
                                  ...storyBible.narration_style,
                                  style_rules: storyBible.narration_style.style_rules.filter((_, idx) => idx !== i)
                                }
                              })}
                            >
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                      <div className="mt-2 flex gap-2">
                        <Input
                          placeholder="Add a style rule (e.g., 'Show don't tell')"
                          id="new-style-rule"
                          className="flex-1 bg-card"
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-cyan-500/35 text-cyan-300"
                          onClick={() => {
                            const input = document.getElementById('new-style-rule');
                            if (input.value.trim()) {
                              setStoryBible({
                                ...storyBible,
                                narration_style: {
                                  ...storyBible.narration_style,
                                  style_rules: [...(storyBible.narration_style?.style_rules || []), input.value.trim()]
                                }
                              });
                              input.value = '';
                            }
                          }}
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                      <p className="text-xs text-cyan-400 mt-1">
                        Examples: "Show don't tell", "Use sensory details", "Keep sentences varied"
                      </p>
                    </div>

                    {/* Style Exclusions - What to NEVER do */}
                    <div>
                      <label className="text-sm font-medium text-red-800 block mb-2">Never Use These Styles</label>
                      <div className="space-y-2">
                        {(storyBible.narration_style?.style_exclusions || []).map((excl, i) => (
                          <div key={i} className="flex items-center justify-between text-sm bg-red-100 p-2 rounded border border-red-200">
                            <span className="text-red-800">❌ {excl}</span>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                              onClick={() => setStoryBible({
                                ...storyBible,
                                narration_style: {
                                  ...storyBible.narration_style,
                                  style_exclusions: storyBible.narration_style.style_exclusions.filter((_, idx) => idx !== i)
                                }
                              })}
                            >
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                      <div className="mt-2 flex gap-2">
                        <Input
                          placeholder="Add style to avoid (e.g., 'No character monologues at chapter starts')"
                          id="new-style-exclusion"
                          className="flex-1 bg-card"
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-red-400 text-red-700"
                          onClick={() => {
                            const input = document.getElementById('new-style-exclusion');
                            if (input.value.trim()) {
                              setStoryBible({
                                ...storyBible,
                                narration_style: {
                                  ...storyBible.narration_style,
                                  style_exclusions: [...(storyBible.narration_style?.style_exclusions || []), input.value.trim()]
                                }
                              });
                              input.value = '';
                            }
                          }}
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                      <p className="text-xs text-red-600 mt-1">
                        Examples: "No self-authoring", "No diatribes at chapter beginnings", "No breaking fourth wall"
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* Planned Reveals & Foreshadowing */}
                <Card className="border-indigo-200 bg-indigo-50">
                  <CardContent className="p-4">
                    <h4 className="font-semibold text-indigo-800 mb-2 flex items-center gap-2">
                      <Lightbulb className="w-4 h-4" /> Planned Reveals & Foreshadowing
                    </h4>
                    {storyBible.planned_reveals?.length > 0 ? (
                      <div className="space-y-2 mb-3">
                        {storyBible.planned_reveals.map((reveal, i) => (
                          <div key={i} className="bg-card p-2 rounded border border-indigo-200">
                            <p className="text-sm font-medium text-indigo-900">Ch {reveal.target_chapter}: {reveal.reveal_description}</p>
                            <p className="text-xs text-indigo-600">{reveal.hints?.length || 0} hints planned</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-indigo-700 italic mb-3">No reveals planned yet</p>
                    )}
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="w-full border-indigo-300 text-indigo-700 hover:bg-indigo-100"
                      onClick={() => onShowForeshadowingModal(true)}
                    >
                      <Lightbulb className="w-3 h-3 mr-1" /> Plan a Reveal
                    </Button>
                  </CardContent>
                </Card>

                {/* ===== NEW: THEMES & SYMBOLS ===== */}
                <Card className="border-emerald-200 bg-emerald-50">
                  <CardContent className="p-4">
                    <h4 className="font-semibold text-emerald-800 mb-3 flex items-center gap-2">
                      <Sparkles className="w-4 h-4" /> Themes & Symbols
                    </h4>
                    
                    {/* Core Message/Theme */}
                    <div className="mb-3">
                      <label className="text-xs font-medium text-emerald-700 block mb-1">Core Theme/Message</label>
                      <Textarea
                        placeholder="What's the main message or theme of your story? (e.g., 'Love conquers all', 'The cost of revenge', 'Finding identity')"
                        value={storyBible.themes_and_symbols?.core_message || ''}
                        onChange={(e) => setStoryBible({
                          ...storyBible,
                          themes_and_symbols: { ...storyBible.themes_and_symbols, core_message: e.target.value }
                        })}
                        className="bg-card text-sm min-h-[50px]"
                      />
                    </div>
                    
                    {/* Symbolic Objects */}
                    <div className="mb-3">
                      <label className="text-xs font-medium text-emerald-700 block mb-1">Symbolic Objects</label>
                      {storyBible.themes_and_symbols?.symbolic_objects?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-2">
                          {storyBible.themes_and_symbols.symbolic_objects.map((obj, i) => (
                            <Badge key={i} variant="outline" className="bg-card border-emerald-300 text-emerald-700">
                              {obj}
                              <button className="ml-1 text-red-400 hover:text-red-600" onClick={() => {
                                const updated = storyBible.themes_and_symbols.symbolic_objects.filter((_, idx) => idx !== i);
                                setStoryBible({
                                  ...storyBible,
                                  themes_and_symbols: { ...storyBible.themes_and_symbols, symbolic_objects: updated }
                                });
                              }}>×</button>
                            </Badge>
                          ))}
                        </div>
                      )}
                      <div className="flex gap-1">
                        <Input
                          placeholder="e.g., 'The old pocket watch'"
                          id="new-symbol"
                          className="text-sm bg-card"
                          onKeyPress={(e) => {
                            if (e.key === 'Enter' && e.target.value.trim()) {
                              setStoryBible({
                                ...storyBible,
                                themes_and_symbols: {
                                  ...storyBible.themes_and_symbols,
                                  symbolic_objects: [...(storyBible.themes_and_symbols?.symbolic_objects || []), e.target.value.trim()]
                                }
                              });
                              e.target.value = '';
                            }
                          }}
                        />
                        <Button size="sm" onClick={() => {
                          const input = document.getElementById('new-symbol');
                          if (input.value.trim()) {
                            setStoryBible({
                              ...storyBible,
                              themes_and_symbols: {
                                ...storyBible.themes_and_symbols,
                                symbolic_objects: [...(storyBible.themes_and_symbols?.symbolic_objects || []), input.value.trim()]
                              }
                            });
                            input.value = '';
                          }
                        }}><Plus className="w-4 h-4" /></Button>
                      </div>
                    </div>
                    
                    {/* Recurring Motifs */}
                    <div>
                      <label className="text-xs font-medium text-emerald-700 block mb-1">Recurring Motifs</label>
                      {storyBible.themes_and_symbols?.recurring_motifs?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-2">
                          {storyBible.themes_and_symbols.recurring_motifs.map((motif, i) => (
                            <Badge key={i} variant="outline" className="bg-card border-emerald-300 text-emerald-700">
                              {motif}
                              <button className="ml-1 text-red-400 hover:text-red-600" onClick={() => {
                                const updated = storyBible.themes_and_symbols.recurring_motifs.filter((_, idx) => idx !== i);
                                setStoryBible({
                                  ...storyBible,
                                  themes_and_symbols: { ...storyBible.themes_and_symbols, recurring_motifs: updated }
                                });
                              }}>×</button>
                            </Badge>
                          ))}
                        </div>
                      )}
                      <div className="flex gap-1">
                        <Input
                          placeholder="e.g., 'Rain during pivotal scenes'"
                          id="new-motif"
                          className="text-sm bg-card"
                          onKeyPress={(e) => {
                            if (e.key === 'Enter' && e.target.value.trim()) {
                              setStoryBible({
                                ...storyBible,
                                themes_and_symbols: {
                                  ...storyBible.themes_and_symbols,
                                  recurring_motifs: [...(storyBible.themes_and_symbols?.recurring_motifs || []), e.target.value.trim()]
                                }
                              });
                              e.target.value = '';
                            }
                          }}
                        />
                        <Button size="sm" onClick={() => {
                          const input = document.getElementById('new-motif');
                          if (input.value.trim()) {
                            setStoryBible({
                              ...storyBible,
                              themes_and_symbols: {
                                ...storyBible.themes_and_symbols,
                                recurring_motifs: [...(storyBible.themes_and_symbols?.recurring_motifs || []), input.value.trim()]
                              }
                            });
                            input.value = '';
                          }
                        }}><Plus className="w-4 h-4" /></Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* ===== NEW: KEY LOCATIONS ===== */}
                <Card className="border-sky-200 bg-sky-50">
                  <CardContent className="p-4">
                    <h4 className="font-semibold text-sky-800 mb-2 flex items-center gap-2">
                      <MapPin className="w-4 h-4" /> Key Locations
                    </h4>
                    {storyBible.locations?.length > 0 && (
                      <div className="space-y-2 mb-3">
                        {storyBible.locations.map((loc, i) => (
                          <div key={i} className="bg-card p-2 rounded border border-sky-200">
                            <div className="flex justify-between items-start">
                              <span className="font-medium text-sm text-sky-900">{loc.name}</span>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-5 w-5 p-0 text-red-500"
                                onClick={() => setStoryBible({
                                  ...storyBible,
                                  locations: storyBible.locations.filter((_, idx) => idx !== i)
                                })}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                            {loc.description && <p className="text-xs text-muted-foreground mt-1">{loc.description}</p>}
                            {loc.atmosphere && <p className="text-xs text-sky-600 italic mt-1">Atmosphere: {loc.atmosphere}</p>}
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="space-y-2">
                      <Input placeholder="Location name" id="new-loc-name" className="text-sm bg-card" />
                      <Input placeholder="Description" id="new-loc-desc" className="text-sm bg-card" />
                      <Input placeholder="Atmosphere (e.g., 'eerie', 'warm and inviting')" id="new-loc-atmo" className="text-sm bg-card" />
                      <Button size="sm" className="w-full" onClick={() => {
                        const name = document.getElementById('new-loc-name').value;
                        const desc = document.getElementById('new-loc-desc').value;
                        const atmo = document.getElementById('new-loc-atmo').value;
                        if (name.trim()) {
                          setStoryBible({
                            ...storyBible,
                            locations: [...(storyBible.locations || []), {
                              name: name.trim(),
                              description: desc.trim(),
                              atmosphere: atmo.trim()
                            }]
                          });
                          document.getElementById('new-loc-name').value = '';
                          document.getElementById('new-loc-desc').value = '';
                          document.getElementById('new-loc-atmo').value = '';
                        }
                      }}>
                        <Plus className="w-4 h-4 mr-1" /> Add Location
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Chapter Summaries - THE KEY TO CONSISTENCY */}
                <Card className="border-cyan-200 bg-cyan-50">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="font-semibold text-cyan-800 flex items-center gap-2">
                        <FileText className="w-4 h-4" /> Chapter Summaries
                      </h4>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="text-xs text-cyan-700"
                        onClick={loadChapterSummaries}
                        disabled={loadingSummaries}
                      >
                        {loadingSummaries ? <RefreshCw className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                      </Button>
                    </div>
                    <p className="text-xs text-cyan-700 mb-3">
                      Shweebum uses these summaries to maintain consistency across chapters.
                    </p>
                    
                    {chapterSummaries.length > 0 ? (
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {chapterSummaries.map((cs) => (
                          <div key={cs.chapter_number} className="bg-card p-2 rounded border border-cyan-200">
                            <div className="flex justify-between items-start">
                              <span className="text-xs font-semibold text-cyan-800">
                                Ch {cs.chapter_number}: {cs.title}
                              </span>
                              <button 
                                onClick={() => regenerateSummary(cs.chapter_number)}
                                className="text-cyan-500 hover:text-cyan-700"
                                title="Regenerate summary"
                              >
                                <RefreshCw className="w-3 h-3" />
                              </button>
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">{cs.summary}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-cyan-600 italic">
                        No chapter summaries yet. They're generated automatically after each chapter is written.
                      </p>
                    )}
                  </CardContent>
                </Card>

                {/* Narrative Tension Tracker */}
                <Card className="border-orange-200 bg-gradient-to-r from-orange-50 to-red-50">
                  <CardContent className="p-4">
                    <h4 className="font-semibold text-orange-800 mb-2 flex items-center gap-2">
                      <Zap className="w-4 h-4" /> Narrative Tension Tracker
                    </h4>
                    
                    {storyBible.tension_curve?.length > 0 ? (
                      <>
                        <p className="text-xs text-cyan-400 mb-3">Drag the sliders to set each chapter's tension (0 = calm, 10 = maximum).</p>
                        {/* Tension bars with sliders */}
                        <div className="bg-card rounded-lg p-3 mb-3 space-y-0">
                          {/* Story Beat Labels */}
                          <div className="flex justify-between text-xs text-muted-foreground mb-1">
                            <span>Setup</span>
                            <span>Rising</span>
                            <span>Midpoint</span>
                            <span>Collapse</span>
                            <span>Climax</span>
                          </div>
                          {/* Bars */}
                          <div className="flex items-end gap-1 h-20">
                            {storyBible.tension_curve.map((t, i) => (
                              <div 
                                key={i} 
                                className="flex-1 rounded-t transition-all relative group"
                                style={{ 
                                  height: `${(t.tension_level / 10) * 100}%`,
                                  backgroundColor: tensionLevelColors[t.tension_level] || '#f97316',
                                  minHeight: '4%'
                                }}
                                title={`Ch ${t.chapter}: ${tensionLevelNames[t.tension_level] || ''} (${t.tension_level}/10)`}
                              >
                                <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 hidden group-hover:block bg-black text-white text-xs px-2 py-1 rounded whitespace-nowrap z-10">
                                  Ch {t.chapter}: {tensionLevelNames[t.tension_level] || ''} ({t.tension_level}/10)
                                </div>
                              </div>
                            ))}
                          </div>
                          {/* Sliders under each bar */}
                          <div className="flex gap-1 mt-2">
                            {storyBible.tension_curve.map((t, i) => (
                              <div key={i} className="flex-1 flex flex-col items-center">
                                <input
                                  type="range"
                                  min="0"
                                  max="10"
                                  value={t.tension_level}
                                  onChange={(e) => {
                                    const newLevel = parseInt(e.target.value);
                                    setStoryBible(prev => {
                                      const updated = [...prev.tension_curve];
                                      updated[i] = {
                                        ...updated[i],
                                        tension_level: newLevel,
                                        tension_name: tensionLevelNames[newLevel] || '',
                                        tension_color: tensionLevelColors[newLevel] || '#f97316'
                                      };
                                      return { ...prev, tension_curve: updated };
                                    });
                                  }}
                                  className="w-full h-1 accent-orange-500 cursor-pointer"
                                  style={{ writingMode: 'vertical-lr', direction: 'rtl', height: '48px', width: '16px' }}
                                  title={`Ch ${t.chapter}: ${t.tension_level}/10`}
                                  data-testid={`tension-slider-${i}`}
                                />
                                <span className="text-[10px] text-muted-foreground mt-0.5">{t.tension_level}</span>
                              </div>
                            ))}
                          </div>
                          <div className="flex justify-between text-xs text-muted-foreground mt-1">
                            <span>Ch 1</span>
                            <span>Ch {storyBible.tension_curve.length}</span>
                          </div>
                        </div>
                        <p className="text-xs text-orange-700">Template: {tensionTemplates[storyBible.tension_template]?.name || 'Custom'}</p>
                        <div className="flex gap-2 mt-2">
                          <Button
                            size="sm"
                            className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
                            data-testid="save-tension-btn"
                            onClick={async () => {
                              try {
                                await axios.post(`${API}/tension/save-curve/${projectId}`, { tension_curve: storyBible.tension_curve, sync_to_outlines: false });
                                toast.success("Tension curve saved!");
                              } catch (e) { toast.error("Failed to save tension"); }
                            }}
                          >
                            <Save className="w-3 h-3 mr-1" /> Save Tension
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1 border-orange-400 text-orange-700 hover:bg-cyan-900/20"
                            data-testid="sync-tension-btn"
                            onClick={async () => {
                              try {
                                const r = await axios.post(`${API}/tension/save-curve/${projectId}`, { tension_curve: storyBible.tension_curve, sync_to_outlines: true }, { timeout: 60000 });
                                toast.success(`Tension saved & ${r.data.outlines_synced} outlines updated!`);
                                // Reload project to refresh outlines
                                const projRes = await axios.get(`${API}/project/${projectId}`);
                                if (projRes.data?.chapter_outline) setProject(prev => ({ ...prev, chapter_outline: projRes.data.chapter_outline }));
                              } catch (e) { toast.error("Failed to sync tension to outlines"); }
                            }}
                          >
                            <RefreshCw className="w-3 h-3 mr-1" /> Sync to Outlines
                          </Button>
                        </div>
                      </>
                    ) : (
                      <p className="text-sm text-orange-700 italic mb-3">No tension curve set - pacing may be inconsistent</p>
                    )}
                    
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="w-full border-cyan-500/30 text-orange-700 hover:bg-cyan-900/20"
                      onClick={() => { loadTensionCurve(); onShowTensionModal(true); }}
                    >
                      <Zap className="w-3 h-3 mr-1" /> {storyBible.tension_curve?.length > 0 ? 'View/Change' : 'Set'} Tension Curve
                    </Button>
                  </CardContent>
                </Card>
              </>
            ) : (
              <div className="text-center py-8">
                <BookMarked className="w-12 h-12 text-slate-300 mb-3" />
                <p className="text-muted-foreground">Story Bible not initialized</p>
                <Button className="mt-4" onClick={loadStoryBible}>Load Story Bible</Button>
              </div>
            )}
          </div>

          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button 
              className="bg-purple-600 hover:bg-purple-700"
              onClick={async () => {
                try {
                  await axios.post(`${API}/story-bible/${projectId}/save`, storyBible);
                  toast.success("Story Bible saved!");
                  onOpenChange(false);
                } catch (e) {
                  toast.error("Failed to save Story Bible");
                }
              }}
            >
              <Save className="w-4 h-4 mr-2" /> Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

  );
};
